<script lang="ts">
  import type { Cell } from '../../lib/labs';

  /**
   * 辞書と Set の盤面（labs.md ラボ 3）。
   * エントリの並びは親が決める（操作のたびに並べ替えて、順序が決まっていないことを見せる）。
   */
  interface Props {
    /** 描く順のエントリ（キーと値）。 */
    lockers: readonly (readonly [string, Cell])[];
    /** Set の要素。null なら Set を描かない。 */
    roster?: readonly Cell[] | null;
    dictName?: string;
    setName?: string;
    /** 光らせるエントリ（今入れた・引いた）。 */
    activeKey?: string | null;
    /** 引いたが無かったキー（空の枠を描く）。 */
    missingKey?: string | null;
    /** 光らせる Set の要素。 */
    activeMember?: Cell | null;
    changeTag?: string | null;
    compact?: boolean;
  }

  let {
    lockers,
    roster = null,
    dictName = 'dict',
    setName = 'seen',
    activeKey = null,
    missingKey = null,
    activeMember = null,
    changeTag = null,
    compact = false,
  }: Props = $props();
</script>

<div class="dict-area" class:compact data-testid="dict-board">
  <div class="shelf">
    <p class="shelf-name">{dictName}<span class="text-soft">（辞書）</span></p>
    <ul class="lockers" aria-label="{dictName} のエントリ（順序は決まっていません）">
      {#if lockers.length === 0 && missingKey === null}
        <li class="empty-note">空です</li>
      {/if}
      {#each lockers as [k, v] (k)}
        <li
          class="locker"
          class:active={k === activeKey}
          aria-label="キー {k}、値 {v}{k === activeKey ? '、いま操作したエントリ' : ''}"
        >
          <span class="tag-key">"{k}"</span>
          <span class="val">{v}</span>
          {#if k === activeKey && changeTag}<span class="change-tag">{changeTag}</span>{/if}
        </li>
      {/each}
      {#if missingKey !== null}
        <li class="locker missing" aria-label="キー {missingKey} はありません。nil">
          <span class="tag-key">"{missingKey}"</span>
          <span class="val">nil</span>
        </li>
      {/if}
    </ul>
  </div>
  {#if roster !== null}
    <div class="shelf">
      <p class="shelf-name">{setName}<span class="text-soft">（Set）</span></p>
      <ul class="roster" aria-label="{setName} の要素">
        {#if roster.length === 0}
          <li class="empty-note">空です</li>
        {/if}
        {#each roster as m (m)}
          <li class="member" class:active={m === activeMember}>{m}</li>
        {/each}
      </ul>
    </div>
  {/if}
</div>

<style>
  .dict-area {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    min-height: 15rem;
    padding: var(--space-4);
    background: var(--canvas);
    border-radius: var(--radius-md);
  }
  .dict-area.compact {
    min-height: 8rem;
    padding: var(--space-3);
    gap: var(--space-3);
  }
  .shelf-name {
    margin: 0 0 var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--fs-sm);
    font-weight: var(--fw-bold);
  }
  .shelf-name span {
    font-family: var(--font-sans);
    font-weight: var(--fw-regular);
  }
  .lockers {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(5.5rem, 1fr));
    gap: var(--space-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .compact .lockers {
    grid-template-columns: repeat(auto-fill, minmax(4.5rem, 1fr));
  }
  .locker {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-2);
    border-radius: var(--radius-md);
    background: var(--surface);
    border: 2px solid var(--border-strong);
    transition:
      background var(--dur-fast),
      transform var(--dur-base) var(--ease-out);
  }
  .locker.active {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
    background: var(--accent-soft);
    animation: open var(--dur-base) var(--ease-out);
  }
  @keyframes open {
    from {
      transform: scale(0.94);
    }
    to {
      transform: none;
    }
  }
  :global(html[data-motion='reduce']) .locker.active {
    animation: none;
  }
  .locker.missing {
    border-style: dashed;
    color: var(--text-soft);
    background: transparent;
  }
  .tag-key {
    font-family: var(--font-mono);
    font-size: var(--fs-xs);
    padding: 0 var(--space-1);
    border-radius: var(--radius-sm);
    background: var(--surface-code);
  }
  .val {
    font-family: var(--font-mono);
    font-size: var(--fs-lg);
    font-weight: var(--fw-bold);
    font-variant-numeric: tabular-nums;
  }
  .compact .val {
    font-size: var(--fs-md);
  }
  .roster {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .member {
    min-width: 2.5rem;
    padding: var(--space-1) var(--space-2);
    border-radius: 999px;
    border: 2px solid var(--border-strong);
    background: var(--surface);
    text-align: center;
    font-family: var(--font-mono);
    font-weight: var(--fw-bold);
  }
  .member.active {
    border-color: var(--accent);
    background: var(--accent-soft);
  }
  .change-tag {
    position: absolute;
    top: -0.7rem;
    right: 0.3rem;
    padding: 0 var(--space-1);
    border-radius: var(--radius-sm);
    background: var(--accent);
    color: var(--text-on-accent);
    font-size: var(--fs-xs);
    font-weight: var(--fw-bold);
  }
  .empty-note {
    color: var(--text-soft);
    font-size: var(--fs-sm);
  }
</style>
